/**
 * @fileoverview Utility for interaction site labels.
 * Uses exhaustive switch statements so that adding a new interaction site
 * in shared_constants.json produces a compile-time error everywhere it
 * needs to be handled.
 * @owner @Appboy/intelligence
 */

import type { I18nT } from "@lib/dashboardI18n";
import { exhaustiveCheck } from "@lib/exhaustiveCheck";

import type { InteractionSite } from "./types";
import { INTERACTION_SITES } from "./types";

/**
 * Returns the translated label for an interaction site.
 *
 * These name a *type*, not one object -- they answer "which Standard type is
 * this?", the same question the digest's "Standard type" column and filter ask,
 * so they must match the Standards-digest table tags and filter options
 * exactly: the QA values name the evaluated object ("Campaign"), not the
 * Standards product. Read them as a standalone label; a caller embedding one
 * mid-sentence should phrase around it rather than expect a full noun phrase.
 *
 * Uses an exhaustive switch so that adding a new interaction site constant
 * to shared_constants.json causes a compile-time error here, prompting the
 * developer to add the new case.
 */
export const interactionSiteLabelsGetter = (t: I18nT<"agent-details">, site: InteractionSite): string => {
  switch (site) {
    case INTERACTION_SITES.CANVAS:
      return t("interaction-site-canvas", "Canvas");
    case INTERACTION_SITES.CATALOG:
      return t("interaction-site-catalog", "Catalog");
    case INTERACTION_SITES.CAMPAIGN_SETUP:
      return t("interaction-site-campaign-setup", "Campaign");
    case INTERACTION_SITES.CANVAS_QA:
      return t("interaction-site-canvas-qa", "Canvas");
    default:
      exhaustiveCheck(site);
      return "Unknown";
  }
};
